import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import GlassCard from "@/components/GlassCard";
import { motion } from "framer-motion";
import { User, Mail, Lock, AlertCircle, Github, Eye, EyeOff } from "lucide-react";
import { API_URL } from "@/lib/api";

function formatError(detail) {
    if (detail == null) return "Something went wrong.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    return String(detail);
}

export default function RegisterPage() {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const { user, register } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (user) navigate("/genz-credit-scoring", { replace: true });
    }, [user, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (password.length < 8) {
            setError("Password must be at least 8 characters");
            return;
        }
        if (!/[A-Za-z]/.test(password) || !/\d/.test(password)) {
            setError("Password must include at least one letter and one number");
            return;
        }
        setSubmitting(true);
        try {
            await register(email, password, name);
            navigate("/genz-credit-scoring", { replace: true });
        } catch (err) {
            setError(formatError(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleOAuth = (provider) => {
        // Bug 4: remember where to return after OAuth completes
        sessionStorage.setItem("oauth_return_to", "/genz-credit-scoring");
        window.location.href = `${API_URL}/api/oauth/${provider}/start`;
    };

    return (
        <div className="min-h-dvh flex items-center justify-center px-6 pt-20 pb-12">
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="w-full max-w-md"
            >
                <div className="text-center mb-8">
                    <h1 className="font-heading text-4xl font-bold tracking-tighter text-text-primary mb-2" data-testid="register-heading">
                        Create account
                    </h1>
                    <p className="text-text-secondary text-base">Get your alternative credit score today</p>
                </div>

                <GlassCard className="p-8" hover={false}>
                    <button
                        onClick={() => handleOAuth("google")}
                        className="w-full flex items-center justify-center gap-3 bg-white text-gray-800 border border-white/20 px-6 py-3 rounded-xl hover:bg-gray-50 hover:shadow-[0_0_15px_rgba(66,133,244,0.3)] transition-all font-medium text-sm mb-3"
                        data-testid="google-register-button"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                        </svg>
                        Continue with Google
                    </button>

                    <button
                        onClick={() => handleOAuth("github")}
                        className="w-full flex items-center justify-center gap-3 bg-elevated text-text-primary border border-white/10 px-6 py-3 rounded-xl hover:bg-white/5 transition-all font-medium text-sm mb-6"
                        data-testid="github-register-button"
                    >
                        <Github size={18} />
                        Sign up with GitHub
                    </button>

                    <div className="flex items-center gap-3 mb-6">
                        <div className="flex-1 h-px bg-white/10" />
                        <span className="text-xs text-text-secondary uppercase tracking-widest">or</span>
                        <div className="flex-1 h-px bg-white/10" />
                    </div>

                    {error && (
                        <div className="flex items-center gap-2 bg-red-starter/10 border border-red-starter/20 rounded-xl px-4 py-3 mb-4" data-testid="register-error">
                            <AlertCircle size={16} className="text-red-starter shrink-0" />
                            <p className="text-sm text-red-starter">{error}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
                        <div>
                            <label htmlFor="register-name" className="text-xs uppercase tracking-[0.2em] text-text-secondary font-semibold mb-2 block">Name</label>
                            <div className="glass-input-wrap">
                                <User size={16} className="field-icon" />
                                <input
                                    id="register-name"
                                    type="text"
                                    name="register_name"
                                    autoComplete="off"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    placeholder="Your name"
                                    data-testid="register-name-input"
                                />
                            </div>
                        </div>
                        <div>
                            <label htmlFor="register-email" className="text-xs uppercase tracking-[0.2em] text-text-secondary font-semibold mb-2 block">Email</label>
                            <div className="glass-input-wrap">
                                <Mail size={16} className="field-icon" />
                                <input
                                    id="register-email"
                                    type="email"
                                    name="register_email"
                                    autoComplete="off"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    required
                                    data-testid="register-email-input"
                                />
                            </div>
                        </div>
                        <div>
                            <label htmlFor="register-password" className="text-xs uppercase tracking-[0.2em] text-text-secondary font-semibold mb-2 block">Password</label>
                            <div className="glass-input-wrap">
                                <Lock size={16} className="field-icon" />
                                <input
                                    id="register-password"
                                    type={showPassword ? "text" : "password"}
                                    name="register_password"
                                    autoComplete="new-password"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    placeholder="Min 8 chars, incl. a number"
                                    required
                                    data-testid="register-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(s => !s)}
                                    className="eye-toggle"
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    data-testid="register-password-toggle"
                                >
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-blue-primary text-white font-medium px-6 py-3 rounded-xl hover:bg-blue-primary/90 hover:shadow-[0_0_20px_rgba(59,130,246,0.4)] transition-all disabled:opacity-50"
                            data-testid="register-submit-button"
                        >
                            {submitting ? "Creating account..." : "Create Account"}
                        </button>
                    </form>
                </GlassCard>

                <p className="text-center text-sm text-text-secondary mt-6">
                    Already have an account?{" "}
                    <Link to="/login" className="text-blue-primary hover:text-blue-primary/80 transition-colors font-medium" data-testid="register-login-link">
                        Sign in
                    </Link>
                </p>
            </motion.div>
        </div>
    );
}
